// src/components/ErrorBoundary.jsx
//
// Global crash net. Without this, any uncaught render error inside a
// lazy-loaded route (and there are 60+ of them in App.js) takes the
// whole React tree down to a permanent white screen — and there is no
// way for the user to recover other than hard-reloading.
//
// We also catch the specific failure mode that bites us hardest in
// production: `ChunkLoadError` (or any "Loading chunk … failed"
// message). After a fresh deploy, browsers with the previous
// index.html cached still hold pointers to chunk filenames that no
// longer exist on the CDN. The first navigation after deploy throws
// `ChunkLoadError`. We detect that, show a tiny "App was updated"
// banner, and reload once with `?cb=` busting any HTML cache so the
// new chunk hashes are picked up.
//
// Visually: a plain white card centred in the viewport with the
// brand wordmark and a single "Reload" button. Intentionally minimal
// so it ships in the eager bundle and never depends on anything that
// might itself fail.
import React from "react";

const ONE_HOUR_MS = 60 * 60 * 1000;
const RELOAD_FLAG_KEY = "lex:eb:lastReloadAt";

function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  if (name === "ChunkLoadError") return true;
  if (/Loading chunk [\d]+ failed/i.test(message)) return true;
  if (/Failed to fetch dynamically imported module/i.test(message)) return true;
  if (/Importing a module script failed/i.test(message)) return true;
  return false;
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, info) {
    // Keep this in console.error specifically — the production
    // console silencer in src/index.js leaves error level intact so
    // monitoring tools (Sentry/etc.) still get the crash.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught error:", error, info?.componentStack);

    // Auto-recover ChunkLoadError once. We rate-limit by writing a
    // timestamp to sessionStorage so a genuinely broken deploy can't
    // trap the user in an infinite reload loop — second time within
    // the hour we just show the manual reload card.
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      try {
        const last = Number(window.sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
        const now = Date.now();
        if (!last || now - last > ONE_HOUR_MS) {
          window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
          // Bust any cached index.html that's still pointing at the
          // old chunk hashes. Use replace() so this reload doesn't
          // sit in the back-button history.
          const url = new URL(window.location.href);
          url.searchParams.set("cb", String(now));
          window.location.replace(url.toString());
        }
      } catch {
        try { window.location.reload(); } catch {}
      }
    }
  }

  handleReload = () => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {}
    try {
      window.location.reload();
    } catch {}
  };

  handleGoHome = () => {
    if (typeof window === "undefined") return;
    try {
      window.location.assign("/");
    } catch {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isChunkError, error } = this.state;
    const headline = isChunkError
      ? "App was updated"
      : "Something went wrong";
    const detail = isChunkError
      ? "A new version of LexTrack AI is available. Please reload to continue."
      : "We hit an unexpected error rendering this page. You can try reloading, or head back to the home page.";

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#fafafa",
          color: "#111",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "#fff",
            border: "1px solid #ececec",
            borderRadius: 12,
            padding: "24px 20px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#888",
              marginBottom: 6,
            }}
          >
            LexTrack AI
          </div>
          <h1 style={{ fontSize: 20, margin: "4px 0 10px", fontWeight: 700 }}>
            {headline}
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "#444",
              margin: "0 0 18px",
            }}
          >
            {detail}
          </p>
          {!isChunkError && process.env.NODE_ENV !== "production" && error?.message ? (
            <pre
              style={{
                textAlign: "left",
                fontSize: 12,
                background: "#f4f4f4",
                color: "#333",
                padding: 12,
                borderRadius: 8,
                overflow: "auto",
                maxHeight: 160,
                margin: "0 0 16px",
              }}
            >
              {String(error.message)}
            </pre>
          ) : null}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                appearance: "none",
                border: "none",
                background: "#111",
                color: "#fff",
                padding: "10px 18px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              style={{
                appearance: "none",
                border: "1px solid #d0d0d0",
                background: "#fff",
                color: "#111",
                padding: "10px 18px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
